import { axiosClientAdmin } from '@/lib/auth';
import { Filter, FilterGroup, FilterOperator, SortOrder } from '@/lib/utils';
import { AddressDto } from '@/types/common-types';

export interface CustomersSearched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  totalRows?: number;
  rows?: CustomerSearchDto[];
  nextPage?: number;
}

export interface CustomerSearchDto {
  // Existing Data
  id: string;
  name: string;
  hasContracts: boolean;
  accountNumber: string;
  archived: boolean;
  email?: string;
  phoneNumber?: string;
  fullname?: string;
  address?: AddressDto;

  // TODO Currently not pulled back
  status: 'active' | 'pending' | 'inactive';
  type: 'enterprise' | 'business' | 'startup';
  location: string;
  sites: number;
  devices: number;

  contact: {
    name: string;
    email: string;
    phone: string;
  };
}

export interface CustomerCreated {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  customerId?: string;
}

export interface CustomerCreateDto {
  name?: string;
  accountNumber?: string;
  fullName?: string;
  address?: AddressDto;
}

export interface CustomerContactDto {
  firstName?: string;
  lastName?: string;
  isBillingContact?: boolean;
  emailAddress?: string;
  phoneNumber?: string;
  roleTitle?: string;
}

export enum PaymentTermType {
  'NONE' = 'NONE',
  'DAYSAFTERBILLDATE' = 'DAYSAFTERBILLDATE',
  'DAYSAFTERBILLMONTH' = 'DAYSAFTERBILLMONTH',
  'OFCURRENTMONTH' = 'OFCURRENTMONTH',
  'OFFOLLOWINGMONTH' = 'OFFOLLOWINGMONTH',
}

export interface CustomerDto {
  id?: string;
  name?: string;
  accountNumber?: string;
  hasContracts?: boolean;
  archived?: boolean;
  salesDueDay?: number;
  salesDueType?: PaymentTermType;
  email?: string;
  phoneNumber?: string;
  xeroId?: string;
  contacts?: CustomerContactDto[];
  monitoringEnabled?: boolean;
  address?: AddressDto;
}

export interface CustomerFetched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  customer?: CustomerDto;
}

export const getCustomer = async (id: string) => {
  try {
    const response = await axiosClientAdmin.get<CustomerFetched>(`/Customer/${id}`);
    console.log(response.data.customer);
    return response.data.customer;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to get customer');
    }
    throw new Error('Failed to get customer');
  }
};

export const searchCustomers = async (
  params: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: SortOrder;
    orderBy?: string;
    filterOperator?: FilterOperator;
    filterFilters?: Filter[];
    filterFilterGroups?: FilterGroup[];
    includeArchived?: boolean;
  } = {}
) => {
  try {
    const requestParams = {
      PageNumber: params.pageNumber,
      PageSize: params.pageSize,
      SortBy: params.sortBy,
      SortDir: params.sortDir,
      OrderBy: params.orderBy,
      'Filter.Operator': params.filterOperator,
      'Filter.Filters': params.filterFilters,
      'Filter.FilterGroups': params.filterFilterGroups,
    };

    const response = await axiosClientAdmin.get<CustomersSearched>('/Customer/search', {
      params: requestParams,
    });

    if (!response.data) {
      throw new Error('No data received from server');
    }

    const totalRows = response.data.totalRows ?? 0;

    if (!params.includeArchived) {
      response.data.rows = response.data.rows?.filter((row) => !row.archived);
    }
    return {
      ...response.data,
      totalRows,
      nextPage:
        (response.data.totalRows || 0) > params.pageNumber! * params.pageSize!
          ? params.pageNumber! + 1
          : undefined,
    };
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to search customers');
    }
    throw new Error('Failed to search customers');
  }
};

export async function createCustomer(data: CustomerCreateDto): Promise<CustomerCreated> {
  const response = await axiosClientAdmin.post<CustomerCreated>('/Customer', data);

  if (!response.data) {
    throw new Error('No data received from server');
  }

  return response.data;
}

export async function updateCustomer(
  id: string,
  data: Partial<CustomerDto>
): Promise<CustomerFetched> {
  const response = await axiosClientAdmin.put<CustomerFetched>(`/Customer/${id}`, data);

  if (!response.data) {
    throw new Error('No data received from server');
  }

  return response.data;
}

export async function archiveCustomer(id: string, archive: boolean): Promise<CustomerFetched> {
  const response = await axiosClientAdmin.put<CustomerFetched>(
    `/Customer/${id}/archive`,
    JSON.stringify(archive)
  );

  if (!response.data) {
    throw new Error('No data received from server');
  }

  return response.data;
}
